import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import './auth.css';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [userType, setUserType] = useState('teacher');
  const [schoolName, setSchoolName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            user_type: userType,
            school_name: schoolName,
          }
        }
      });

      if (error) throw error;
      
      // Create profile entry in profiles table
      if (data?.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            {
              id: data.user.id,
              full_name: fullName,
              user_type: userType,
              school_name: schoolName,
              email: email,
            }
          ]);

        if (profileError) {
          console.error('Profile creation error:', profileError);
        }
      }
      
      navigate('/login', { 
        state: { message: 'Account created! Please check your email to confirm your account.' } 
      });
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        {/* Left Side - Welcome Section - UPDATED for Teach IDD */}
        <div className="welcome-section">
          <div className="welcome-content">
            <h1 className="welcome-title">
              Join Teachers Who<br />
              <span className="brand-gradient">Support Every Learner</span>
            </h1>
            <p className="welcome-subtitle">
              Create your Teach IDD account and get AI-powered tools to help slow learners 
              and students with intellectual and developmental disabilities succeed.
            </p>
            <div className="feature-highlights">
              <div className="highlight-item">
                <span className="highlight-icon">🧠</span>
                <span>IDD Teaching Guide</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">📖</span>
                <span>Confusion Solver</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">🗣️</span>
                <span>Read-Aloud Mode</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">📋</span>
                <span>No Diagnosis Required</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div className="form-section">
          <div className="form-container">
            <div className="form-header">
              <h2 className="form-title">Teach IDD</h2>
              <p className="form-subtitle">Create your teacher account</p>
            </div>

            <form onSubmit={handleSignup} className="auth-form">
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
              
              <div className="input-group">
                <label htmlFor="fullName">Full Name</label>
                <div className="input-wrapper">
                  <input
                    id="fullName"
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="form-input"
                  />
                  <span className="input-icon">👤</span>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-wrapper">
                  <input
                    id="email"
                    type="email"
                    placeholder="teacher@school.edu.ng"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-input"
                  />
                  <span className="input-icon">📧</span>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="userType">I am a...</label>
                <div className="input-wrapper">
                  <select
                    id="userType"
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    required
                    className="form-input"
                  >
                    <option value="teacher">Teacher</option>
                    <option value="special_educator">Special Educator</option>
                    <option value="parent">Parent / Caregiver</option>
                    <option value="aide">Classroom Aide / Assistant</option>
                  </select>
                  <span className="input-icon">👥</span>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="schoolName">School Name (Optional)</label>
                <div className="input-wrapper">
                  <input
                    id="schoolName"
                    type="text"
                    placeholder="e.g., Blessed Brain International School"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    className="form-input"
                  />
                  <span className="input-icon">🏫</span>
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password (min. 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="form-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="input-wrapper">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="form-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              
              <button 
                type="submit" 
                disabled={loading}
                className={`login-btn ${loading ? 'loading' : ''}`}
              >
                {loading ? (
                  <span className="loading-content">
                    <span className="spinner"></span>
                    Creating Account...
                  </span>
                ) : (
                  'Start Teaching with AI'
                )}
              </button>
            </form>

            <div className="form-footer">
              <p>
                Already have an account? 
                <Link to="/login" className="signup-link">
                  Sign In to Teach IDD
                </Link>
              </p>
            </div>

            <div className="info-note">
              <p className="info-text">
                💡 No special training required. Teach IDD helps you support every learner — even without a formal diagnosis.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;